import React, { useEffect, useRef, useState } from 'react';
import {
  Paper,
  Typography,
  Box,
  Alert,
  FormControlLabel,
  Switch,
  TextField,
  CircularProgress,
} from '@mui/material';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import { useTranslation } from 'react-i18next';
import { getGeneralConfig, saveGeneralConfig } from '../../../services/ecommerceApi';
import { isValidWhatsAppPhone, normalizeWhatsAppPhone } from '../utils/whatsappPhone';
import { notifyEcommerceSaved } from '../utils/ecommerceSaveToast';

interface WhatsAppSettingsProps {
  companyId: string | null;
}

const DEFAULT_MESSAGE = 'Merhaba! Web sitenizden yazıyorum.';

const WhatsAppSettings: React.FC<WhatsAppSettingsProps> = ({ companyId }) => {
  const { t } = useTranslation('salesSystem');
  const [enabled, setEnabled] = useState(false);
  const [phone, setPhone] = useState('');
  const [message, setMessage] = useState(DEFAULT_MESSAGE);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const loadedRef = useRef(false);

  useEffect(() => {
    const load = async () => {
      if (!companyId) return;
      setLoading(true);
      try {
        const response = await getGeneralConfig(companyId);
        if (response?.data) {
          setEnabled(!!response.data.whatsAppEnabled);
          setPhone(response.data.whatsAppPhone || '');
          setMessage(response.data.whatsAppMessage || DEFAULT_MESSAGE);
        }
        loadedRef.current = true;
      } catch (error) {
        console.error('❌ [WHATSAPP] Ayarlar yüklenemedi:', error);
        setFeedback({
          type: 'error',
          message: t('ecommerceGeneral.whatsapp.loadError'),
        });
      } finally {
        setLoading(false);
      }
    };

    load();

    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, [companyId, t]);

  const persist = async (next: { enabled: boolean; phone: string; message: string }) => {
    if (!companyId) return;

    setSaving(true);
    setFeedback(null);
    try {
      const normalizedPhone = normalizeWhatsAppPhone(next.phone);
      await saveGeneralConfig({
        companyId,
        whatsAppEnabled: next.enabled,
        whatsAppPhone: normalizedPhone || next.phone.trim(),
        whatsAppMessage: next.message.trim(),
      });
      if (normalizedPhone && normalizedPhone !== next.phone.replace(/[^\d]/g, '')) {
        setPhone(normalizedPhone);
      }
      notifyEcommerceSaved(t('ecommerceGeneral.autoSave.savedToast'));
      setFeedback(null);
    } catch (error) {
      console.error('❌ [WHATSAPP] Ayar kaydedilemedi:', error);
      setFeedback({
        type: 'error',
        message: t('ecommerceGeneral.whatsapp.saveError'),
      });
    } finally {
      setSaving(false);
    }
  };

  const schedulePersist = (next: { enabled: boolean; phone: string; message: string }) => {
    if (!loadedRef.current || !companyId) return;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      persist(next);
    }, 500);
  };

  const handleEnabledChange = (checked: boolean) => {
    setEnabled(checked);
    schedulePersist({ enabled: checked, phone, message });
  };

  const handlePhoneChange = (value: string) => {
    setPhone(value);
    schedulePersist({ enabled, phone: value, message });
  };

  const handleMessageChange = (value: string) => {
    setMessage(value);
    schedulePersist({ enabled, phone, message: value });
  };

  const phoneValid = isValidWhatsAppPhone(phone);

  return (
    <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <WhatsAppIcon sx={{ mr: 1.5, color: '#25D366' }} />
        <Typography variant="h6">{t('ecommerceGeneral.whatsapp.title')}</Typography>
        {saving && <CircularProgress size={18} sx={{ ml: 1.5 }} />}
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('ecommerceGeneral.whatsapp.help')}
      </Typography>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={28} />
        </Box>
      ) : (
        <>
          <FormControlLabel
            control={
              <Switch
                checked={enabled}
                onChange={(e) => handleEnabledChange(e.target.checked)}
                disabled={!companyId}
                color="success"
              />
            }
            label={enabled ? t('ecommerceGeneral.whatsapp.enabled') : t('ecommerceGeneral.whatsapp.disabled')}
            sx={{ mb: 2 }}
          />

          {enabled && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                label={t('ecommerceGeneral.whatsapp.phoneLabel')}
                value={phone}
                onChange={(e) => handlePhoneChange(e.target.value)}
                placeholder={t('ecommerceGeneral.whatsapp.phonePlaceholder')}
                helperText={t('ecommerceGeneral.whatsapp.phoneHelp')}
                type="tel"
                fullWidth
                required
                error={enabled && phone.length > 0 && !phoneValid}
                disabled={!companyId}
                InputProps={{
                  startAdornment: (
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: '50%',
                        bgcolor: '#25D366',
                        color: '#fff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mr: 1,
                        flexShrink: 0,
                      }}
                    >
                      <WhatsAppIcon sx={{ fontSize: 20 }} />
                    </Box>
                  ),
                }}
              />

              <TextField
                label={t('ecommerceGeneral.whatsapp.messageLabel')}
                value={message}
                onChange={(e) => handleMessageChange(e.target.value)}
                helperText={t('ecommerceGeneral.whatsapp.messageHelp')}
                fullWidth
                multiline
                minRows={2}
                disabled={!companyId}
              />

              {enabled && !phoneValid && (
                <Alert severity="warning">{t('ecommerceGeneral.whatsapp.phoneRequired')}</Alert>
              )}

              {enabled && phoneValid && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    p: 2,
                    border: '1px solid #e0e0e0',
                    borderRadius: 1,
                    bgcolor: '#f9fafb',
                  }}
                >
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: '50%',
                      bgcolor: '#25D366',
                      color: '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 4px 20px rgba(37, 211, 102, 0.4)',
                    }}
                  >
                    <WhatsAppIcon sx={{ fontSize: 28 }} />
                  </Box>
                  <Box>
                    <Typography variant="subtitle2">{t('ecommerceGeneral.whatsapp.previewTitle')}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t('ecommerceGeneral.whatsapp.previewHelp')}
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </>
      )}

      {feedback && (
        <Alert severity={feedback.type} sx={{ mt: 2 }} onClose={() => setFeedback(null)}>
          {feedback.message}
        </Alert>
      )}
    </Paper>
  );
};

export default WhatsAppSettings;
